import React, { useState } from 'react';
import { Eye, EyeOff, Mail, Lock, User, ArrowLeft, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { supabase } from '../lib/supabase';

type AuthMode = 'login' | 'signup' | 'forgot-password' | 'reset-password' | 'verify-email';

interface AuthPageProps {
  onAuthSuccess: (user: any) => void;
}

export const AuthPage: React.FC<AuthPageProps> = ({ onAuthSuccess }) => {
  const [mode, setMode] = useState<AuthMode>('login');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Form fields
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [username, setUsername] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [resetCode, setResetCode] = useState('');

  const clearForm = () => {
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setUsername('');
    setResetCode('');
    setError(null);
    setSuccess(null);
    setShowPassword(false);
    setShowConfirmPassword(false);
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    // Validation
    if (!username.trim()) {
      setError('Please enter a username');
      return;
    }
    if (username.trim().length < 3) {
      setError('Username must be at least 3 characters');
      return;
    }
    if (!email.trim()) {
      setError('Please enter your email');
      return;
    }
    if (!password) {
      setError('Please enter a password');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      const result = await supabase!.auth.signUp({
        email: email.trim().toLowerCase(),
        password: password,
        options: { data: { name: username.trim() } },
      });

      if (result.error) {
        setError(result.error.message || 'Sign up failed. Please try again.');
      } else {
        setSuccess('Account created! Please check your email to verify your account, then log in.');
        setTimeout(() => {
          clearForm();
          setMode('login');
        }, 3000);
      }
    } catch (err: any) {
      setError(err.message || 'An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!email.trim()) {
      setError('Please enter your email');
      return;
    }
    if (!password) {
      setError('Please enter your password');
      return;
    }

    setLoading(true);
    try {
      const result = await supabase!.auth.signInWithPassword({
        email: email.trim().toLowerCase(),
        password: password,
      });

      if (result.error) {
        setError(result.error.message || 'Login failed. Please check your credentials.');
      } else if (result.data?.user) {
        setSuccess('Login successful!');
        setTimeout(() => {
          onAuthSuccess(result.data.user);
        }, 500);
      }
    } catch (err: any) {
      setError(err.message || 'An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }

    setLoading(true);
    try {
      const { error: resetError } = await supabase!.auth.resetPasswordForEmail(
        email.trim().toLowerCase(),
        { redirectTo: 'https://www.dipps.ca/#/reset-password' }
      );
      if (resetError) throw resetError;

      setSuccess('Password reset code sent! Check your email inbox.');
      setTimeout(() => {
        setMode('reset-password');
        setError(null);
        setSuccess(null);
      }, 2000);
    } catch (err: any) {
      setError(err.message || 'Failed to send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!resetCode.trim()) {
      setError('Please enter the reset code from your email');
      return;
    }
    if (!password) {
      setError('Please enter a new password');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      const result = await supabase!.auth.verifyOtp({
        email: email.trim().toLowerCase(),
        token: resetCode.trim(),
        type: 'recovery',
      });

      if (result.error) {
        setError(result.error.message || 'Password reset failed. Please try again.');
      } else {
        // Update the password after OTP verification
        await supabase!.auth.updateUser({ password });
        setSuccess('Password reset successful! You can now log in with your new password.');
        setTimeout(() => {
          clearForm();
          setMode('login');
        }, 3000);
      }
    } catch (err: any) {
      setError(err.message || 'An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const renderPasswordInput = (
    value: string,
    onChange: (value: string) => void,
    show: boolean,
    setShow: (show: boolean) => void,
    placeholder: string,
    id: string
  ) => (
    <div className="relative">
      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
        <Lock className="text-text-muted" size={20} />
      </div>
      <input
        type={show ? 'text' : 'password'}
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full pl-12 pr-12 py-4 text-lg rounded-2xl border-2 border-accent/30 bg-background focus:border-primary focus:outline-none transition-colors"
        autoComplete={id === 'password' ? 'current-password' : 'new-password'}
      />
      <button
        type="button"
        onClick={() => setShow(!show)}
        className="absolute inset-y-0 right-0 pr-4 flex items-center text-text-muted hover:text-primary transition-colors"
        aria-label={show ? 'Hide password' : 'Show password'}
      >
        {show ? <EyeOff size={20} /> : <Eye size={20} />}
      </button>
    </div>
  );

  return (
    <div className="w-full max-w-md mx-auto">
      {/* Header */}
      {mode !== 'login' && (
        <button
          onClick={() => {
            clearForm();
            setMode('login');
          }}
          className="flex items-center gap-2 text-primary mb-4 hover:underline"
        >
          <ArrowLeft size={20} />
          <span>Back to Login</span>
        </button>
      )}

      {/* Error Message */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-2xl flex items-start gap-3">
          <AlertCircle className="text-red-500 flex-shrink-0 mt-0.5" size={20} />
          <p className="text-red-700 dark:text-red-300 text-sm">{error}</p>
        </div>
      )}

      {/* Success Message */}
      {success && (
        <div className="mb-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-2xl flex items-start gap-3">
          <CheckCircle className="text-green-500 flex-shrink-0 mt-0.5" size={20} />
          <p className="text-green-700 dark:text-green-300 text-sm">{success}</p>
        </div>
      )}

      {/* LOGIN FORM */}
      {mode === 'login' && (
        <form onSubmit={handleLogin} className="space-y-4">
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-primary font-serif">Welcome Back</h2>
            <p className="text-text-secondary text-sm mt-1">Sign in to continue</p>
          </div>

          {/* Email */}
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <Mail className="text-text-muted" size={20} />
            </div>
            <input
              type="email"
              id="login-email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email address"
              className="w-full pl-12 pr-4 py-4 text-lg rounded-2xl border-2 border-accent/30 bg-background focus:border-primary focus:outline-none transition-colors"
              autoComplete="email"
            />
          </div>

          {/* Password */}
          {renderPasswordInput(password, setPassword, showPassword, setShowPassword, 'Password', 'password')}

          {/* Forgot Password Link */}
          <div className="text-right">
            <button
              type="button"
              onClick={() => {
                setError(null);
                setSuccess(null);
                setMode('forgot-password');
              }}
              className="text-primary text-sm hover:underline"
            >
              Forgot your password?
            </button>
          </div>

          {/* Login Button */}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-primary text-white py-4 rounded-2xl text-lg font-bold shadow-lg hover:bg-primary/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {loading && <Loader2 className="animate-spin" size={20} />}
            {loading ? 'Signing in...' : 'Sign In'}
          </button>

          {/* Switch to Sign Up */}
          <div className="text-center pt-4 border-t border-accent/20">
            <p className="text-text-secondary text-sm">
              Don't have an account?{' '}
              <button
                type="button"
                onClick={() => {
                  clearForm();
                  setMode('signup');
                }}
                className="text-primary font-bold hover:underline"
              >
                Sign up now
              </button>
            </p>
          </div>
        </form>
      )}

      {/* SIGNUP FORM */}
      {mode === 'signup' && (
        <form onSubmit={handleSignUp} className="space-y-4">
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-primary font-serif">Create Account</h2>
            <p className="text-text-secondary text-sm mt-1">Join our community</p>
          </div>

          {/* Username */}
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <User className="text-text-muted" size={20} />
            </div>
            <input
              type="text"
              id="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Choose a username"
              className="w-full pl-12 pr-4 py-4 text-lg rounded-2xl border-2 border-accent/30 bg-background focus:border-primary focus:outline-none transition-colors"
              autoComplete="username"
            />
          </div>

          {/* Email */}
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <Mail className="text-text-muted" size={20} />
            </div>
            <input
              type="email"
              id="signup-email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email address"
              className="w-full pl-12 pr-4 py-4 text-lg rounded-2xl border-2 border-accent/30 bg-background focus:border-primary focus:outline-none transition-colors"
              autoComplete="email"
            />
          </div>

          {/* Password */}
          {renderPasswordInput(password, setPassword, showPassword, setShowPassword, 'Create password (min 6 characters)', 'new-password')}

          {/* Confirm Password */}
          {renderPasswordInput(confirmPassword, setConfirmPassword, showConfirmPassword, setShowConfirmPassword, 'Confirm password', 'confirm-password')}

          {/* Sign Up Button */}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-primary text-white py-4 rounded-2xl text-lg font-bold shadow-lg hover:bg-primary/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {loading && <Loader2 className="animate-spin" size={20} />}
            {loading ? 'Creating account...' : 'Create Account'}
          </button>

          {/* Switch to Login */}
          <div className="text-center pt-4 border-t border-accent/20">
            <p className="text-text-secondary text-sm">
              Already have an account?{' '}
              <button
                type="button"
                onClick={() => {
                  clearForm();
                  setMode('login');
                }}
                className="text-primary font-bold hover:underline"
              >
                Sign in
              </button>
            </p>
          </div>
        </form>
      )}

      {/* FORGOT PASSWORD FORM */}
      {mode === 'forgot-password' && (
        <form onSubmit={handleForgotPassword} className="space-y-4">
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-primary font-serif">Reset Password</h2>
            <p className="text-text-secondary text-sm mt-1">Enter your email to receive a reset code</p>
          </div>

          {/* Email */}
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <Mail className="text-text-muted" size={20} />
            </div>
            <input
              type="email"
              id="reset-email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Your email address"
              className="w-full pl-12 pr-4 py-4 text-lg rounded-2xl border-2 border-accent/30 bg-background focus:border-primary focus:outline-none transition-colors"
              autoComplete="email"
            />
          </div>

          {/* Send Reset Code Button */}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-primary text-white py-4 rounded-2xl text-lg font-bold shadow-lg hover:bg-primary/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {loading && <Loader2 className="animate-spin" size={20} />}
            {loading ? 'Sending...' : 'Send Reset Code'}
          </button>
        </form>
      )}

      {/* RESET PASSWORD FORM */}
      {mode === 'reset-password' && (
        <form onSubmit={handleResetPassword} className="space-y-4">
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-primary font-serif">Enter New Password</h2>
            <p className="text-text-secondary text-sm mt-1">Check your email for the reset code</p>
          </div>

          {/* Reset Code */}
          <div>
            <input
              type="text"
              id="reset-code"
              value={resetCode}
              onChange={(e) => setResetCode(e.target.value)}
              placeholder="Enter 8-digit code from email"
              className="w-full px-4 py-4 text-lg rounded-2xl border-2 border-accent/30 bg-background focus:border-primary focus:outline-none transition-colors text-center tracking-widest"
              maxLength={8}
              autoComplete="one-time-code"
            />
          </div>

          {/* New Password */}
          {renderPasswordInput(password, setPassword, showPassword, setShowPassword, 'New password', 'new-password')}

          {/* Confirm Password */}
          {renderPasswordInput(confirmPassword, setConfirmPassword, showConfirmPassword, setShowConfirmPassword, 'Confirm new password', 'confirm-password')}

          {/* Reset Password Button */}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-primary text-white py-4 rounded-2xl text-lg font-bold shadow-lg hover:bg-primary/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {loading && <Loader2 className="animate-spin" size={20} />}
            {loading ? 'Resetting...' : 'Reset Password'}
          </button>

          {/* Resend Code */}
          <div className="text-center">
            <button
              type="button"
              onClick={handleForgotPassword}
              disabled={loading}
              className="text-primary text-sm hover:underline"
            >
              Didn't receive the code? Send again
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default AuthPage;
